"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import type { DE_BROGLIE_WAVELENGTH_CALCULATOR_STRINGS } from "@/components/tools/de-broglie-wavelength-calculator";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { ScaleStrip } from "@/components/tools/physics-waves/scale-strip";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  ATOMIC_MASS_UNIT,
  classicalError,
  fromKinetic,
  fromMomentum,
  fromSpeed,
  fromVoltage,
  type Method,
  PARTICLES,
  type ParticleId,
  type Wave,
  type WaveResult,
} from "@/lib/tool-kit/physics-waves/broglie";
import {
  ELEMENTARY_CHARGE,
  SPEED_OF_LIGHT,
} from "@/lib/tool-kit/physics-waves/electromagnetic";
import {
  formatSig,
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  ENERGY_UNITS,
  findUnit,
  SPEED_UNITS,
  toBase,
  type UnitDef,
  VOLT_UNITS,
} from "@/lib/tool-kit/physics-waves/units";

export type BroglieStrings = typeof DE_BROGLIE_WAVELENGTH_CALCULATOR_STRINGS;

type Known = "speed" | "kinetic" | "voltage" | "momentum";

interface Entry {
  value: string;
  unit: string;
}

interface State {
  particle: ParticleId;
  mass: Entry;
  charge: string;
  known: Known;
  speed: Entry;
  kinetic: Entry;
  voltage: Entry;
  momentum: Entry;
  method: Method;
}

const MASS_UNITS: UnitDef[] = [
  { id: "kg", symbol: "kg", exp: 0 },
  { id: "g", symbol: "g", exp: -3 },
  { id: "u", symbol: "u", exp: 0, factor: ATOMIC_MASS_UNIT },
];
const SPEED_WITH_C: UnitDef[] = [
  ...SPEED_UNITS,
  { id: "c", symbol: "c", exp: 0, factor: SPEED_OF_LIGHT },
];
const MOMENTUM_UNITS: UnitDef[] = [
  { id: "kgms", symbol: "kg·m/s", exp: 0 },
  {
    id: "eV/c",
    symbol: "eV/c",
    exp: 0,
    factor: ELEMENTARY_CHARGE / SPEED_OF_LIGHT,
  },
  {
    id: "keV/c",
    symbol: "keV/c",
    exp: 3,
    factor: ELEMENTARY_CHARGE / SPEED_OF_LIGHT,
  },
  {
    id: "MeV/c",
    symbol: "MeV/c",
    exp: 6,
    factor: ELEMENTARY_CHARGE / SPEED_OF_LIGHT,
  },
];
const SIG_ROWS = 4;

const INITIAL: State = {
  particle: "electron",
  mass: { value: "1", unit: "kg" },
  charge: "1",
  known: "voltage",
  speed: { value: "1e6", unit: "m/s" },
  kinetic: { value: "54", unit: "eV" },
  voltage: { value: "54", unit: "V" },
  momentum: { value: "1", unit: "keV/c" },
  method: "relativistic",
};

type PresetKey = keyof BroglieStrings["presets"];

const PRESETS: Record<PresetKey, Partial<State>> = {
  davisson: {
    particle: "electron",
    known: "voltage",
    voltage: { value: "54", unit: "V" },
  },
  microscope: {
    particle: "electron",
    known: "voltage",
    voltage: { value: "100", unit: "kV" },
  },
  neutron: {
    particle: "neutron",
    known: "kinetic",
    kinetic: { value: "0.025", unit: "eV" },
  },
  alpha: {
    particle: "alpha",
    known: "kinetic",
    kinetic: { value: "5", unit: "MeV" },
  },
  c60: {
    particle: "custom",
    mass: { value: "720", unit: "u" },
    charge: "0",
    known: "speed",
    speed: { value: "220", unit: "m/s" },
  },
  baseball: {
    particle: "custom",
    mass: { value: "0.145", unit: "kg" },
    charge: "0",
    known: "speed",
    speed: { value: "40", unit: "m/s" },
  },
};

function readEntry(entry: Entry, units: readonly UnitDef[]): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

function particleProps(state: State): {
  mass: number | null;
  charge: number | null;
} {
  if (state.particle === "custom") {
    return {
      mass: readEntry(state.mass, MASS_UNITS),
      charge: parseNumberInput(state.charge),
    };
  }
  const found = PARTICLES.find((p) => p.id === state.particle);
  return { mass: found?.mass ?? null, charge: found?.charge ?? null };
}

function solve(
  state: State,
  mass: number,
  charge: number,
  method: Method
): WaveResult | "missing" {
  if (state.known === "speed") {
    const v = readEntry(state.speed, SPEED_WITH_C);
    return v === null ? "missing" : fromSpeed(mass, v, method);
  }
  if (state.known === "kinetic") {
    const k = readEntry(state.kinetic, ENERGY_UNITS);
    return k === null ? "missing" : fromKinetic(mass, k, method);
  }
  if (state.known === "voltage") {
    const v = readEntry(state.voltage, VOLT_UNITS);
    return v === null ? "missing" : fromVoltage(mass, charge, v, method);
  }
  const p = readEntry(state.momentum, MOMENTUM_UNITS);
  return p === null ? "missing" : fromMomentum(mass, p, method);
}

function BroglieResults({
  wave,
  other,
  method,
  strings,
  fmt,
  sig,
}: {
  wave: Wave;
  other: Wave | null;
  method: Method;
  strings: BroglieStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const plain = (value: number, unit: string) =>
    `${formatSig(fmt, value, sig)} ${unit}`;
  const exact = method === "relativistic" ? wave : other;
  const classical = method === "classical" ? wave : other;
  const error =
    exact !== null && classical !== null
      ? classicalError(exact, classical)
      : null;
  const wavelengthText = show(wave.wavelength, "m");
  const lines = [
    t(strings.stepWavelength, {
      h: "6.62607015 × 10⁻³⁴ J·s",
      p: plain(wave.momentum, "kg·m/s"),
      lambda: wavelengthText,
    }),
  ];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.wavelength} value={wavelengthText} />
        <StatCard
          label={strings.momentum}
          value={plain(wave.momentum, "kg·m/s")}
        />
        <StatCard
          label={strings.kinetic}
          value={show(wave.kinetic / ELEMENTARY_CHARGE, "eV")}
        />
        <StatCard
          label={strings.kineticJoules}
          value={plain(wave.kinetic, "J")}
        />
        <StatCard label={strings.speed} value={plain(wave.speed, "m/s")} />
        <StatCard label={strings.beta} value={formatSig(fmt, wave.beta, sig)} />
        {method === "relativistic" ? (
          <StatCard
            label={strings.gamma}
            value={formatSig(fmt, wave.gamma, SIG_ROWS + 1)}
          />
        ) : null}
        {error === null ? null : (
          <StatCard
            label={strings.classicalError}
            value={`${error > 0 ? "+" : ""}${formatSig(fmt, error, 3)}%`}
          />
        )}
      </StatGrid>
      <ScaleStrip
        description={strings.scaleDescription}
        highExp={-3}
        label={strings.scaleLabel}
        lowExp={-36}
        marks={[
          { value: 8.4e-16, label: strings.markProton },
          { value: 1e-10, label: strings.markAtom },
          { value: 5.5e-7, label: strings.markLight },
        ]}
        value={wave.wavelength}
        valueLabel={t(strings.scaleValue, { value: wavelengthText })}
      />
      <WorkingSteps heading={strings.working} lines={lines} />
      {method === "classical" && wave.beta > 0.1 ? (
        <Notice>{strings.fastNote}</Notice>
      ) : null}
    </>
  );
}

function BroglieBody({
  state,
  strings,
  fmt,
  sig,
}: {
  state: State;
  strings: BroglieStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const { mass, charge } = particleProps(state);
  if (mass === null || charge === null || mass <= 0) {
    return <Notice>{strings.enterValue}</Notice>;
  }
  const primary = solve(state, mass, charge, state.method);
  if (primary === "missing") {
    return <Notice>{strings.enterValue}</Notice>;
  }
  if (!primary.ok) {
    if (state.known === "voltage" && charge === 0) {
      return <ErrorNotice>{strings.neutralNoVoltage}</ErrorNotice>;
    }
    return (
      <ErrorNotice>
        {primary.error === "too-fast" ? strings.tooFast : strings.invalid}
      </ErrorNotice>
    );
  }
  const secondary = solve(
    state,
    mass,
    charge,
    state.method === "relativistic" ? "classical" : "relativistic"
  );
  return (
    <BroglieResults
      fmt={fmt}
      method={state.method}
      other={secondary !== "missing" && secondary.ok ? secondary.wave : null}
      sig={sig}
      strings={strings}
      wave={primary.wave}
    />
  );
}

export function BrogliePanel({
  strings,
  fmt,
  sig,
}: {
  strings: BroglieStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [state, setState] = useState(INITIAL);
  const set = (patch: Partial<State>) =>
    setState((current) => ({ ...current, ...patch }));
  const entryKey = state.known;
  const units = {
    speed: SPEED_WITH_C,
    kinetic: ENERGY_UNITS,
    voltage: VOLT_UNITS,
    momentum: MOMENTUM_UNITS,
  }[entryKey];
  const labels = {
    speed: strings.speedInput,
    kinetic: strings.kineticInput,
    voltage: strings.voltageInput,
    momentum: strings.momentumInput,
  };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="db-particle"
          label={strings.particleLabel}
          onChange={(particle) => set({ particle })}
          options={[
            ...PARTICLES.map((p) => ({
              value: p.id as ParticleId,
              label: strings.particles[p.id],
            })),
            { value: "custom" as ParticleId, label: strings.customParticle },
          ]}
          value={state.particle}
        />
        <PillGroup
          label={strings.knownLabel}
          name="db-known"
          onChange={(known) => set({ known })}
          options={(Object.keys(strings.knowns) as Known[]).map((value) => ({
            value,
            label: strings.knowns[value],
          }))}
          value={state.known}
        />
      </div>
      {state.particle === "custom" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <QuantityField
            id="db-mass"
            label={strings.massInput}
            onChange={(value) => set({ mass: { ...state.mass, value } })}
            onUnitChange={(unit) => set({ mass: { ...state.mass, unit } })}
            unit={state.mass.unit}
            unitLabel={t(strings.unitFor, { name: strings.massInput })}
            units={MASS_UNITS}
            value={state.mass.value}
          />
          {state.known === "voltage" ? (
            <TextField
              id="db-charge"
              label={strings.chargeInput}
              numeric
              onChange={(value) => set({ charge: value })}
              value={state.charge}
            />
          ) : null}
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id={`db-${entryKey}`}
          label={labels[entryKey]}
          onChange={(value) =>
            set({ [entryKey]: { ...state[entryKey], value } })
          }
          onUnitChange={(unit) =>
            set({ [entryKey]: { ...state[entryKey], unit } })
          }
          unit={state[entryKey].unit}
          unitLabel={t(strings.unitFor, { name: labels[entryKey] })}
          units={units}
          value={state[entryKey].value}
        />
        <PillGroup
          label={strings.methodLabel}
          name="db-method"
          onChange={(method) => set({ method })}
          options={[
            { value: "relativistic", label: strings.methodExact },
            { value: "classical", label: strings.methodClassical },
          ]}
          value={state.method}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
          <button
            className={BUTTON_SMALL}
            key={key}
            onClick={() => set(PRESETS[key])}
            type="button"
          >
            {strings.presets[key]}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <BroglieBody fmt={fmt} sig={sig} state={state} strings={strings} />
      </section>
    </>
  );
}
